import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Beaker, ListChecks, Gamepad2, Globe, PenLine, Radio, Tv,
  Users, Trophy, Brain, Music, Palette, BookOpen, FlaskConical, Mic,
  Video, Star, Clock, ChevronRight, Dices, Calculator, Puzzle, Target,
  Calendar, CheckCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { SchoolRadioPlayer } from "../components/common/SchoolRadioPlayer";

const btn3DStyle = "shadow-[0_6px_0_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_0_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.06)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.04)] active:translate-y-[5px] transition-all duration-150 ease-out";

const PageShell = ({ icon: Icon, title, subtitle, color, children }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400">
      <div className={`bg-gradient-to-r ${color} text-white py-10`}>
        <div className="max-w-5xl mx-auto px-4">
          <button onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-3 border border-white/20 ${btn3DStyle} cursor-pointer select-none`}
            data-testid="back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center"><Icon size={24} /></div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">{title}</h1>
              <p className="text-white/80 text-sm">{subtitle}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {children}
        {/* Bottom Back Button */}
        <div className="text-center mt-8">
          <button onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-2 px-6 py-3 bg-white text-[#002147] font-semibold rounded-xl border-2 border-[#002147]/20 ${btn3DStyle} cursor-pointer select-none`}
            data-testid="back-btn-bottom">
            <ArrowLeft size={18} /> Back
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ STEM ACTIVITIES ============
export const STEMActivitiesPage = () => {
  const activities = [
    { title: "Robotics Club", desc: "Build and program robots using Arduino and Raspberry Pi kits. Weekly sessions every Wednesday.", icon: Beaker, level: "JHS", color: "bg-violet-100 text-violet-600" },
    { title: "Science Fair Projects", desc: "Prepare exhibits for the annual inter-school science fair. Mentorship available from science teachers.", icon: FlaskConical, level: "All Levels", color: "bg-blue-100 text-blue-600" },
    { title: "Coding Workshop", desc: "Learn Scratch (KG-Class 3) and Python (Class 4-JHS). Every Friday after school.", icon: Brain, level: "All Levels", color: "bg-green-100 text-green-600" },
    { title: "Math Olympiad Training", desc: "Competitive mathematics problem-solving sessions for district and regional competitions.", icon: Calculator, level: "Class 4 - JHS", color: "bg-orange-100 text-orange-600" },
    { title: "Environmental Science Club", desc: "Tree planting, recycling projects, and nature walks. Learn about ecosystems and conservation.", icon: Globe, level: "All Levels", color: "bg-emerald-100 text-emerald-600" },
    { title: "STEM Innovation Lab", desc: "Access the school's lab for hands-on experiments in physics, chemistry, and biology.", icon: Beaker, level: "JHS", color: "bg-pink-100 text-pink-600" },
  ];
  return (
    <PageShell icon={Beaker} title="STEM Activities" subtitle="Science, Technology, Engineering & Mathematics" color="from-violet-600 to-purple-500">
      <div className="grid md:grid-cols-2 gap-5">
        {activities.map((a, i) => (
          <Card key={i} className="hover:shadow-lg transition-shadow" data-testid={`stem-activity-${i}`}>
            <CardContent className="p-5 flex gap-4">
              <div className={`w-12 h-12 rounded-xl ${a.color} flex items-center justify-center flex-shrink-0`}><a.icon size={22} /></div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-[#002147]">{a.title}</h3>
                  <Badge variant="outline" className="text-xs">{a.level}</Badge>
                </div>
                <p className="text-sm text-gray-600">{a.desc}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </PageShell>
  );
};

// ============ SCHOOL AGENDA ============
export const SchoolAgendaPage = () => {
  const terms = [
    {
      name: "Term 1 (September - December)", items: [
        { date: "Sep 4", event: "School Reopening" }, { date: "Sep 15", event: "PTA Meeting" },
        { date: "Oct 1", event: "Independence Day Celebration" }, { date: "Oct 20", event: "Mid-Term Exams" },
        { date: "Nov 10", event: "Inter-House Sports" }, { date: "Nov 25", event: "Science & Math Week" },
        { date: "Dec 6", event: "End of Term Exams Begin" }, { date: "Dec 20", event: "Speech & Prize-Giving Day" },
      ]
    },
    {
      name: "Term 2 (January - April)", items: [
        { date: "Jan 8", event: "School Reopening" }, { date: "Jan 20", event: "Spelling Bee Competition" },
        { date: "Feb 14", event: "Founders Day Celebration" }, { date: "Feb 28", event: "Mid-Term Exams" },
        { date: "Mar 6", event: "Ghana Independence Day" }, { date: "Mar 15", event: "Career Day" },
        { date: "Apr 1", event: "End of Term Exams Begin" }, { date: "Apr 12", event: "Easter Break" },
      ]
    },
    {
      name: "Term 3 (May - July)", items: [
        { date: "May 6", event: "School Reopening" }, { date: "May 17", event: "Cultural Day" },
        { date: "Jun 1", event: "Children's Day Celebration" }, { date: "Jun 14", event: "BECE Mock Exams (JHS 3)" },
        { date: "Jun 28", event: "End of Term Exams Begin" }, { date: "Jul 5", event: "KG Graduation" },
        { date: "Jul 12", event: "JHS Graduation & Valedictory" }, { date: "Jul 19", event: "Long Vacation Begins" },
      ]
    }
  ];
  return (
    <PageShell icon={ListChecks} title="School Agenda" subtitle="Academic Calendar & Important Dates" color="from-emerald-600 to-teal-500">
      <div className="space-y-6">
        {terms.map((term, ti) => (
          <Card key={ti} data-testid={`agenda-term-${ti}`}>
            <CardHeader className="bg-[#002147] text-white rounded-t-lg py-3">
              <CardTitle className="text-lg">{term.name}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {term.items.map((item, ii) => (
                <div key={ii} className={`flex items-center gap-4 px-5 py-3 ${ii % 2 === 0 ? "bg-white" : "bg-gray-50"} ${ii === term.items.length - 1 ? "" : "border-b"}`}>
                  <span className="w-16 text-sm font-bold text-[#FF6B00] flex-shrink-0">{item.date}</span>
                  <span className="text-sm text-[#002147]">{item.event}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </PageShell>
  );
};

// ============ GAMES ============
export const GamesPage = () => {
  const [selectedLevel, setSelectedLevel] = useState("all");
  const levels = ["all", "Nursery-KG", "Class 1-3", "Class 4-6", "JHS"];
  const games = [
    { title: "Math Puzzle Challenge", desc: "Solve addition, subtraction, and multiplication puzzles against the clock!", icon: Calculator, level: "Nursery-KG", color: "bg-pink-100 text-pink-600", link: "https://www.mathplayground.com/grade_1_games.html" },
    { title: "Letter & Word Match", desc: "Match letters, sight words, and build vocabulary with fun drag-and-drop games.", icon: Puzzle, level: "Nursery-KG", color: "bg-purple-100 text-purple-600", link: "https://www.starfall.com/h/index.htm" },
    { title: "Counting Adventure", desc: "Learn to count from 1-100 with colorful characters and interactive stories.", icon: Dices, level: "Nursery-KG", color: "bg-yellow-100 text-yellow-600", link: "https://www.topmarks.co.uk/maths-games/5-7-years/counting" },
    { title: "Times Tables Quest", desc: "Master multiplication tables through speed challenges and reward badges.", icon: Target, level: "Class 1-3", color: "bg-blue-100 text-blue-600", link: "https://www.timestables.com/" },
    { title: "Spelling Bee Practice", desc: "Practice spelling words organized by grade level with audio pronunciation.", icon: BookOpen, level: "Class 1-3", color: "bg-green-100 text-green-600", link: "https://www.spellingtraining.com/" },
    { title: "Science Quiz Bowl", desc: "Test your knowledge of plants, animals, weather, and the human body.", icon: FlaskConical, level: "Class 1-3", color: "bg-orange-100 text-orange-600", link: "https://www.education.com/games/science/" },
    { title: "Fraction & Decimal Games", desc: "Interactive games to master fractions, decimals, and percentages.", icon: Calculator, level: "Class 4-6", color: "bg-teal-100 text-teal-600", link: "https://www.mathplayground.com/index_fractions.html" },
    { title: "Geography Explorer", desc: "Explore maps, learn about countries, capitals, and physical features of the world.", icon: Globe, level: "Class 4-6", color: "bg-indigo-100 text-indigo-600", link: "https://www.geoguessr.com/free" },
    { title: "Grammar Challenge", desc: "Parts of speech, sentence structure, and comprehension activities.", icon: PenLine, level: "Class 4-6", color: "bg-rose-100 text-rose-600", link: "https://www.education.com/games/grammar/" },
    { title: "Algebra & Equations", desc: "Solve algebraic expressions and equations with step-by-step guidance.", icon: Brain, level: "JHS", color: "bg-violet-100 text-violet-600", link: "https://www.khanacademy.org/math/algebra-basics" },
    { title: "Integrated Science Quiz", desc: "Physics, chemistry, and biology quiz games aligned with JHS curriculum.", icon: Beaker, level: "JHS", color: "bg-cyan-100 text-cyan-600", link: "https://quizizz.com/admin/quiz/science" },
    { title: "English Comprehension", desc: "Reading passages with questions to improve comprehension and critical thinking.", icon: BookOpen, level: "JHS", color: "bg-amber-100 text-amber-600", link: "https://www.education.com/games/reading/" },
  ];
  const filtered = selectedLevel === "all" ? games : games.filter(g => g.level === selectedLevel);
  return (
    <PageShell icon={Gamepad2} title="Educational Games" subtitle="Learning through play - organized by class level" color="from-rose-500 to-pink-500">
      <div className="flex flex-wrap gap-2 mb-6">
        {levels.map(l => (
          <button key={l} onClick={() => setSelectedLevel(l)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedLevel === l ? "bg-[#002147] text-white" : "bg-white/80 text-[#002147] hover:bg-white"}`}
            data-testid={`game-filter-${l}`}>{l === "all" ? "All Levels" : l}</button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((g, i) => (
          <Card key={i} className="hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer group" data-testid={`game-card-${i}`}
            onClick={() => window.open(g.link, "_blank")}>
            <CardContent className="p-5">
              <div className={`w-12 h-12 rounded-xl ${g.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}><g.icon size={22} /></div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-[#002147]">{g.title}</h3>
              </div>
              <Badge variant="outline" className="text-xs mb-2">{g.level}</Badge>
              <p className="text-sm text-gray-600">{g.desc}</p>
              <div className="mt-3 text-xs text-[#FF6B00] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">Play Now <ChevronRight size={12} /></div>
            </CardContent>
          </Card>
        ))}
      </div>
    </PageShell>
  );
};

// ============ SCHOOL CLUBS ============
export const SchoolClubsPage = () => {
  const navigate = useNavigate();
  const btn3D = "shadow-[0_6px_0_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_0_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.06)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.04)] active:translate-y-[5px] transition-all duration-150 ease-out";
  const clubs = [
    { id: "chess-club", name: "Chess Club", desc: "Learn strategies, compete in tournaments, and sharpen your mind. Open to Class 3 and above.", icon: Brain, day: "Monday", time: "3:30 - 5:00 PM", color: "bg-slate-100 text-slate-600", image: "https://images.unsplash.com/photo-1765851802854-e577d9e6b48b?w=400&q=60" },
    { id: "debate-club", name: "Debate Club", desc: "Develop public speaking, argumentation, and critical thinking skills.", icon: Mic, day: "Tuesday", time: "3:30 - 5:00 PM", color: "bg-blue-100 text-blue-600", image: "https://images.unsplash.com/photo-1660351174962-e2a1fbb9af09?w=400&q=60" },
    { id: "drama-theatre-club", name: "Drama & Theatre Club", desc: "Act, direct, and produce plays for school events and competitions.", icon: Star, day: "Wednesday", time: "3:30 - 5:00 PM", color: "bg-pink-100 text-pink-600", image: "https://images.pexels.com/photos/6896324/pexels-photo-6896324.jpeg?auto=compress&w=400" },
    { id: "music-club", name: "Music Club", desc: "Learn instruments, vocal training, and perform at school assemblies and events.", icon: Music, day: "Thursday", time: "3:30 - 5:00 PM", color: "bg-purple-100 text-purple-600", image: "https://images.pexels.com/photos/31762320/pexels-photo-31762320.jpeg?auto=compress&w=400" },
    { id: "art-craft-club", name: "Art & Craft Club", desc: "Painting, sculpture, weaving, and creative projects. Exhibitions held every term.", icon: Palette, day: "Friday", time: "3:30 - 5:00 PM", color: "bg-orange-100 text-orange-600", image: "https://images.unsplash.com/photo-1615909340810-3ec0e50f9e4d?w=400&q=60" },
    { id: "tennis-club", name: "Tennis Club", desc: "Learn tennis fundamentals, play matches, and represent the school in inter-school competitions.", icon: Trophy, day: "Mon & Wed", time: "4:00 - 5:30 PM", color: "bg-green-100 text-green-600", image: "https://images.unsplash.com/photo-1725680647573-9737175af6c4?w=400&q=60" },
    { id: "football-club", name: "Football Club", desc: "Training sessions, friendly matches, and inter-school football tournaments.", icon: Trophy, day: "Tue & Thu", time: "4:00 - 5:30 PM", color: "bg-emerald-100 text-emerald-600", image: "https://images.pexels.com/photos/274422/pexels-photo-274422.jpeg?auto=compress&w=400" },
    { id: "science-club", name: "Science Club", desc: "Experiments, science fairs, and exciting demonstrations. For curious minds!", icon: FlaskConical, day: "Wednesday", time: "3:30 - 5:00 PM", color: "bg-cyan-100 text-cyan-600", image: "https://images.pexels.com/photos/5427865/pexels-photo-5427865.jpeg?auto=compress&w=400" },
    { id: "literary-reading-club", name: "Literary & Reading Club", desc: "Book discussions, creative writing, poetry recitals, and reading challenges.", icon: BookOpen, day: "Friday", time: "3:30 - 4:30 PM", color: "bg-amber-100 text-amber-600", image: "https://images.pexels.com/photos/10638214/pexels-photo-10638214.jpeg?auto=compress&w=400" },
    { id: "environmental-club", name: "Environmental Club", desc: "Tree planting, clean-up campaigns, and environmental awareness projects.", icon: Globe, day: "Saturday", time: "8:00 - 10:00 AM", color: "bg-lime-100 text-lime-600", image: "https://images.unsplash.com/photo-1758585734153-132c8620c1bd?w=400&q=60" },
    { id: "cadet-corps", name: "Cadet Corps", desc: "Discipline, leadership, drills, and community service activities.", icon: Target, day: "Saturday", time: "7:00 - 10:00 AM", color: "bg-red-100 text-red-600", image: "https://images.unsplash.com/photo-1472691777234-99bdb20e973c?w=400&q=60" },
    { id: "ict-coding-club", name: "ICT & Coding Club", desc: "Computer skills, web design, app development, and digital literacy.", icon: Brain, day: "Thursday", time: "3:30 - 5:00 PM", color: "bg-violet-100 text-violet-600", image: "https://images.pexels.com/photos/5530437/pexels-photo-5530437.jpeg?auto=compress&w=400" },
  ];
  return (
    <PageShell icon={Users} title="School Clubs" subtitle="Join a club, discover your passion" color="from-sky-600 to-blue-500">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {clubs.map((c, i) => (
          <button key={i} onClick={() => navigate(`/school-clubs/${c.id}`)}
            className={`bg-white rounded-2xl overflow-hidden text-left ${btn3D} cursor-pointer select-none`}
            data-testid={`club-card-${i}`}>
            <div className="h-32 overflow-hidden">
              <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-9 h-9 rounded-lg ${c.color} flex items-center justify-center`}><c.icon size={18} /></div>
                <h3 className="font-bold text-[#002147]">{c.name}</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">{c.desc}</p>
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge variant="outline" className="text-xs flex items-center gap-1"><Clock size={10} /> {c.day}</Badge>
                <Badge className="bg-[#FF6B00]/10 text-[#FF6B00] text-xs">{c.time}</Badge>
              </div>
              <div className="text-xs text-[#FF6B00] font-semibold flex items-center gap-1">View Details & Enroll <ChevronRight size={12} /></div>
            </div>
          </button>
        ))}
      </div>
      {/* Duplicate back button removed — PageShell already provides one */}
    </PageShell>
  );
};

// ============ BLOG ============
export const BlogPage = () => {
  const posts = [
    { title: "YCPS Students Shine at Regional Science Fair", date: "March 15, 2026", category: "Achievements", excerpt: "Our JHS students brought home 3 gold medals and 2 silver medals at the Western Region Science Fair, showcasing projects on renewable energy and water purification.", image: "bg-blue-200" },
    { title: "New STEM Lab Opens at Main Campus", date: "March 1, 2026", category: "Campus News", excerpt: "The state-of-the-art STEM laboratory is now fully operational with equipment for robotics, electronics, and advanced science experiments.", image: "bg-green-200" },
    { title: "Inter-House Sports Day Highlights", date: "February 20, 2026", category: "Sports", excerpt: "Red House emerged champions at this year's Inter-House Sports competition. Over 500 students participated in track events, football, and volleyball.", image: "bg-orange-200" },
    { title: "Tips for Parents: Supporting Your Child's Homework", date: "February 10, 2026", category: "Parenting", excerpt: "Our guidance counselors share practical tips on creating a productive homework environment and encouraging independent learning habits.", image: "bg-purple-200" },
    { title: "Class 6 Excursion to Kakum National Park", date: "January 28, 2026", category: "Excursions", excerpt: "Students explored the canopy walkway, learned about rainforest ecosystems, and documented wildlife species for their science projects.", image: "bg-teal-200" },
    { title: "Founders Day Celebration: 56 Years of Excellence", date: "January 15, 2026", category: "Events", excerpt: "Alumni, students, and staff gathered to celebrate 56 years of educational excellence. Special recognition was given to outstanding alumni.", image: "bg-pink-200" },
    { title: "JHS 3 BECE Preparation Programme Launched", date: "January 5, 2026", category: "Academics", excerpt: "A comprehensive after-school programme has been launched to prepare JHS 3 students for the upcoming BECE examinations with extra tutorials and mock tests.", image: "bg-yellow-200" },
    { title: "Debate Team Wins District Championship", date: "December 18, 2025", category: "Achievements", excerpt: "Our debate team argued their way to victory in the district championship, defeating 8 other schools. They advance to the regional finals.", image: "bg-indigo-200" },
  ];
  return (
    <PageShell icon={PenLine} title="School Blog" subtitle="News, stories, and updates from YCPS" color="from-fuchsia-600 to-pink-500">
      <div className="grid md:grid-cols-2 gap-6">
        {posts.map((p, i) => (
          <Card key={i} className="overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer" data-testid={`blog-post-${i}`}>
            <div className={`h-32 ${p.image} flex items-end p-4`}>
              <Badge className="bg-white text-[#002147] shadow">{p.category}</Badge>
            </div>
            <CardContent className="p-5">
              <p className="text-xs text-gray-400 mb-1">{p.date}</p>
              <h3 className="font-bold text-[#002147] mb-2 text-lg">{p.title}</h3>
              <p className="text-sm text-gray-600">{p.excerpt}</p>
              <p className="mt-3 text-sm text-[#FF6B00] font-medium flex items-center gap-1">Read More <ChevronRight size={14} /></p>
            </CardContent>
          </Card>
        ))}
      </div>
    </PageShell>
  );
};

// ============ SCHOOL PODCAST ============
const podcastEpisodes = [
  { id: "ep01", ep: "EP 01", title: "Welcome to YCPS Podcast!", desc: "Our headmaster introduces the school's new podcast and shares the vision for the academic year.", duration: "15 min", date: "Mar 10, 2026",
    fullContent: "In this inaugural episode, the Headmaster welcomes listeners to the very first YCPS Podcast. He shares the school's vision for the 2025/2026 academic year, highlighting new programs including the expanded STEM lab, the new assignment portal, and exciting plans for inter-school competitions. He also discusses the school's commitment to holistic education - developing not just academic excellence but character, faith, and community spirit.",
    guests: ["Headmaster - YCPS"], topics: ["School vision 2025/2026", "New academic programs", "STEM Lab opening", "Holistic education philosophy"],
    links: [{ title: "School Website", url: "https://www.ycpschool.com" }, { title: "Enrollment Information", url: "/resources/enrollment-application" }] },
  { id: "ep02", ep: "EP 02", title: "Study Tips from Top Students", desc: "JHS 3 students who scored top marks share their study strategies and time management tips.", duration: "22 min", date: "Mar 3, 2026",
    fullContent: "Three of our top-performing JHS 3 students sit down to share the study habits that helped them achieve outstanding results. They discuss creating effective study timetables, the Pomodoro technique, active recall methods, and how to balance academics with extracurricular activities. They also share advice for younger students preparing for the BECE.",
    guests: ["JHS 3 Student 1", "JHS 3 Student 2", "JHS 3 Student 3"], topics: ["Study timetable creation", "Pomodoro technique", "Active recall methods", "BECE preparation tips", "Balancing academics and activities"],
    links: [{ title: "Study Tips PDF", url: "#" }, { title: "JHS Exams Prep", url: "/jhs-exams-prep" }] },
  { id: "ep03", ep: "EP 03", title: "Career Day Highlights", desc: "Interviews with professionals who visited during Career Day - doctors, engineers, and entrepreneurs.", duration: "18 min", date: "Feb 24, 2026",
    fullContent: "Career Day 2026 was a massive success with over 15 professionals visiting our campus. In this episode, we feature interviews with a Medical Doctor, a Civil Engineer, and a Tech Entrepreneur. They share their career journeys, what inspired them, the challenges they overcame, and practical advice for students considering these career paths.",
    guests: ["Guest Speaker - Medical Doctor", "Guest Speaker - Civil Engineer", "Guest Speaker - Tech Entrepreneur"], topics: ["Medicine as a career", "Engineering pathways", "Entrepreneurship", "Education requirements", "Career planning advice"],
    links: [{ title: "Career Day Photos", url: "/gallery" }, { title: "School Clubs", url: "/school-clubs" }] },
  { id: "ep04", ep: "EP 04", title: "The History of YCPS", desc: "A journey through 56 years of excellence - from our founding in 1970 to the present day.", duration: "25 min", date: "Feb 17, 2026",
    fullContent: "Take a walk down memory lane as we explore the rich history of Young Christian Preparatory School. Founded in 1970, YCPS has grown from a small school with 30 students to a thriving institution with two campuses. We hear from founding-era alumni who share memories of the early days, the school's growth through the decades, major milestones, and the enduring Christian values that have guided the school for 56 years.",
    guests: ["Alumni Representative - Class of 1975", "Retired Teacher (1978-2010)"], topics: ["Founding story (1970)", "Growth through the decades", "Major milestones", "Alumni memories", "Christian values and tradition"],
    links: [{ title: "School Gallery", url: "/gallery" }, { title: "About YCPS", url: "/about" }] },
  { id: "ep05", ep: "EP 05", title: "Sports Talk: Inter-House Competition", desc: "Team captains discuss preparation, highlights, and sportsmanship from this year's competition.", duration: "20 min", date: "Feb 10, 2026",
    fullContent: "The annual Inter-House Sports Competition was one for the books! In this episode, the four house captains - representing Red, Blue, Green, and Yellow houses - discuss their preparation strategies, share the most exciting moments from the competition, and reflect on the importance of sportsmanship. Red House emerged as overall champions, but every house showed incredible spirit and teamwork.",
    guests: ["Red House Captain", "Blue House Captain", "Green House Captain", "Yellow House Captain"], topics: ["House preparation strategies", "Competition highlights", "Track & field results", "Football tournament", "Sportsmanship and teamwork"],
    links: [{ title: "Sports Photos", url: "/gallery" }, { title: "Athletics Page", url: "/athletics" }] },
  { id: "ep06", ep: "EP 06", title: "Science Fair Preparation Guide", desc: "Science teachers explain how to choose a project topic, conduct research, and present findings.", duration: "17 min", date: "Feb 3, 2026",
    fullContent: "With the Regional Science Fair approaching, our science teachers provide a comprehensive guide for students. They cover how to choose an impactful project topic, the scientific method, designing experiments, collecting and analyzing data, creating effective display boards, and tips for presenting to judges with confidence. Past winning projects are discussed as examples.",
    guests: ["Head of Science Department", "Biology Teacher"], topics: ["Choosing a project topic", "Scientific method", "Experiment design", "Data collection & analysis", "Presentation skills", "Past winning projects"],
    links: [{ title: "Science Buddies", url: "https://www.sciencebuddies.org/" }, { title: "Science Club", url: "/school-clubs/science-club" }, { title: "STEM Activities", url: "/stem-activities" }] },
];

export const SchoolPodcastPage = () => {
  const navigate = useNavigate();
  const [isListening, setIsListening] = useState(false);
  return (
    <PageShell icon={Radio} title="School Podcast" subtitle="Listen, learn, and stay connected" color="from-amber-600 to-orange-500">
      {/* LIVE PODCAST WINDOW */}
      <Card className="mb-8 overflow-hidden shadow-2xl border-0" data-testid="live-podcast-window">
        <div className="relative bg-gradient-to-br from-[#1a0a00] via-[#2d1400] to-[#1a0a00] min-h-[400px]">
          {/* Frame */}
          <div className="absolute inset-0 border-[6px] border-gray-800 rounded-xl" />
          <div className="absolute inset-[6px] border border-amber-900/30 rounded-lg" />

          {/* Screen Content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
            {/* LIVE Badge */}
            <div className="absolute top-6 left-6 flex items-center gap-2">
              <div className="flex items-center gap-1.5 bg-red-600 text-white px-3 py-1 rounded-md text-xs font-bold animate-pulse">
                <div className="w-2 h-2 bg-white rounded-full" />
                LIVE
              </div>
              <span className="text-white/50 text-xs">YCPS Podcast Studio</span>
            </div>

            {!isListening ? (
              <div className="text-center">
                {/* Microphone / Radio visual */}
                <div className="relative w-36 h-36 mx-auto mb-6">
                  <div className="absolute inset-0 bg-[#FF6B00]/10 rounded-full animate-ping" style={{ animationDuration: "3s" }} />
                  <div className="absolute inset-3 bg-[#FF6B00]/10 rounded-full animate-ping" style={{ animationDuration: "2s", animationDelay: "0.5s" }} />
                  <div className="relative w-full h-full bg-gradient-to-br from-[#FF6B00]/20 to-orange-900/20 rounded-full flex items-center justify-center border-2 border-[#FF6B00]/20">
                    <Radio size={52} className="text-[#FF6B00]/60" />
                  </div>
                </div>
                <h2 className="text-white text-xl font-bold mb-1">YCPS Podcast Live</h2>
                <p className="text-white/50 text-sm mb-6">Young Christian Preparatory School Audio Channel</p>
                <button onClick={() => setIsListening(true)}
                  className={`px-8 py-4 bg-[#FF6B00] hover:bg-orange-600 text-white font-bold rounded-2xl ${btn3DStyle} cursor-pointer select-none flex items-center gap-3 mx-auto`}
                  data-testid="podcast-listen-btn">
                  <Radio size={20} />
                  Listen Live
                </button>
              </div>
            ) : (
              <div className="text-center">
                {/* Audio visualizer bars */}
                <div className="flex items-end justify-center gap-1 h-20 mb-6">
                  {[40,65,50,80,60,75,45,70,55,80,65,50,75,60,45].map((h, i) => (
                    <div key={i} className="w-2 bg-gradient-to-t from-[#FF6B00] to-amber-400 rounded-t-full animate-pulse opacity-60"
                      style={{ height: `${h}%`, animationDelay: `${i * 0.1}s`, animationDuration: `${0.5 + Math.random() * 0.5}s` }} />
                  ))}
                </div>
                <h2 className="text-white text-lg font-semibold mb-1">No Live Podcast Right Now</h2>
                <p className="text-white/40 text-sm mb-2">The next live episode will be announced soon</p>
                <p className="text-white/30 text-xs">Browse recorded episodes below</p>
                <button onClick={() => setIsListening(false)}
                  className={`mt-4 px-6 py-2 bg-white/10 text-white/70 font-medium rounded-xl ${btn3DStyle} cursor-pointer select-none border border-white/10`}
                  data-testid="podcast-stop-btn">
                  Back to Home Screen
                </button>
              </div>
            )}

            {/* Bottom info bar */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#FF6B00] rounded-lg flex items-center justify-center">
                  <Mic size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">YCPS Podcast</p>
                  <p className="text-white/50 text-xs">School Audio Broadcasting</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-white/40 text-xs">
                <Clock size={12} /> Live &middot; Takoradi, Ghana
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* School Radio Player */}
      <div className="mb-8" data-testid="school-radio-section">
        <h2 className="text-xl font-bold text-[#002147] mb-4 flex items-center gap-2"><Radio size={22} /> School Radio</h2>
        <SchoolRadioPlayer />
      </div>

      {/* Episodes List */}
      <h2 className="text-xl font-bold text-[#002147] mb-4 flex items-center gap-2"><Radio size={22} /> All Episodes</h2>
      <div className="space-y-4">
        {podcastEpisodes.map((ep, i) => (
          <button key={i} onClick={() => navigate(`/school-podcast/${ep.id}`)}
            className={`w-full text-left bg-white rounded-2xl ${btn3DStyle} cursor-pointer select-none`}
            data-testid={`podcast-ep-${i}`}>
            <div className="p-5 flex items-center gap-5">
              <div className="w-16 h-16 bg-gradient-to-br from-[#FF6B00] to-orange-400 rounded-2xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md">{ep.ep}</div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-[#002147] mb-1">{ep.title}</h3>
                <p className="text-sm text-gray-600 mb-2 truncate">{ep.desc}</p>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span className="flex items-center gap-1"><Clock size={10} /> {ep.duration}</span>
                  <span>{ep.date}</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-[#002147] rounded-full flex items-center justify-center text-white flex-shrink-0 group-hover:bg-[#003366]">
                <Radio size={20} />
              </div>
            </div>
          </button>
        ))}
      </div>
      <Card className="mt-6 bg-[#002147] text-white">
        <CardContent className="p-6 text-center">
          <h3 className="font-bold text-lg mb-2">Want to be on the podcast?</h3>
          <p className="text-white/70 text-sm">Students, teachers, and parents are welcome to suggest topics or be guests. Contact the media team at media@ycpschool.com</p>
        </CardContent>
      </Card>
    </PageShell>
  );
};

export const PodcastEpisodePage = () => {
  const { episodeId } = useParams();
  const navigate = useNavigate();
  const episode = podcastEpisodes.find(e => e.id === episodeId);

  if (!episode) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-bold text-[#002147] mb-3">Episode Not Found</h2>
          <button onClick={() => navigate("/school-podcast")} className={`px-6 py-3 bg-[#FF6B00] text-white font-semibold rounded-xl ${btn3DStyle}`}>
            <ArrowLeft size={16} className="inline mr-2" /> Back to Podcast
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="podcast-episode-page">
      <div className="bg-gradient-to-r from-amber-600 to-orange-500 text-white py-10">
        <div className="max-w-5xl mx-auto px-4">
          <button onClick={() => navigate("/school-podcast")}
            className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-4 border border-white/20 ${btn3DStyle} cursor-pointer select-none`}
            data-testid="back-btn-top">
            <ArrowLeft size={16} /> Back to All Episodes
          </button>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg">{episode.ep}</div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">{episode.title}</h1>
              <div className="flex items-center gap-3 mt-2 text-white/80 text-sm">
                <span className="flex items-center gap-1"><Clock size={14} /> {episode.duration}</span>
                <span className="flex items-center gap-1"><Calendar size={14} /> {episode.date}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Episode Content */}
        <Card className="shadow-lg" data-testid="episode-content">
          <CardHeader><CardTitle className="text-[#002147] flex items-center gap-2"><Radio size={20} /> Episode Summary</CardTitle></CardHeader>
          <CardContent><p className="text-gray-700 leading-relaxed">{episode.fullContent}</p></CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Guests */}
          <Card className="shadow-lg" data-testid="episode-guests">
            <CardHeader><CardTitle className="text-[#002147] flex items-center gap-2"><Users size={20} /> Featured Guests</CardTitle></CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {episode.guests.map((g, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-gray-700">
                    <div className="w-9 h-9 bg-[#FF6B00]/10 rounded-full flex items-center justify-center"><Users size={16} className="text-[#FF6B00]" /></div>
                    {g}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Topics */}
          <Card className="shadow-lg" data-testid="episode-topics">
            <CardHeader><CardTitle className="text-[#002147] flex items-center gap-2"><ListChecks size={20} /> Topics Covered</CardTitle></CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {episode.topics.map((t, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle size={14} className="text-green-500 flex-shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Related Links */}
        {episode.links.length > 0 && (
          <Card className="shadow-lg" data-testid="episode-links">
            <CardHeader><CardTitle className="text-[#002147] flex items-center gap-2"><Globe size={20} /> Related Links</CardTitle></CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {episode.links.map((link, i) => (
                  <a key={i} href={link.url.startsWith("http") ? link.url : undefined}
                    onClick={(e) => { if (!link.url.startsWith("http")) { e.preventDefault(); navigate(link.url); } }}
                    target={link.url.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className={`p-4 rounded-xl bg-white border-2 border-gray-100 text-left ${btn3DStyle} cursor-pointer select-none block`}
                    data-testid={`episode-link-${i}`}>
                    <span className="text-sm font-semibold text-[#002147] flex items-center gap-2">
                      <ChevronRight size={14} className="text-[#FF6B00]" /> {link.title}
                    </span>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bottom Back Button */}
        <div className="text-center py-4">
          <button onClick={() => navigate("/school-podcast")}
            className={`inline-flex items-center gap-2 px-6 py-3 bg-white text-[#002147] font-semibold rounded-xl border-2 border-[#002147]/20 ${btn3DStyle} cursor-pointer select-none`}
            data-testid="back-btn-bottom">
            <ArrowLeft size={18} /> Back to All Episodes
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ SCHOOL TV ============
export const SchoolTVPage = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const channels = [
    { title: "Morning Assembly Highlights", desc: "Watch recordings of morning devotions, announcements, and special presentations.", category: "Assembly", color: "bg-blue-100 text-blue-600" },
    { title: "Classroom Showcase", desc: "See what students are learning! Recordings of special lessons and demonstrations.", category: "Academics", color: "bg-green-100 text-green-600" },
    { title: "Sports Highlights", desc: "Relive the best moments from inter-house competitions, football matches, and sports day.", category: "Sports", color: "bg-orange-100 text-orange-600" },
    { title: "Cultural Day Performances", desc: "Watch traditional dances, songs, and cultural displays from our annual celebrations.", category: "Culture", color: "bg-purple-100 text-purple-600" },
    { title: "Graduation Ceremonies", desc: "KG graduation, JHS valedictory service, and prize-giving day recordings.", category: "Events", color: "bg-pink-100 text-pink-600" },
    { title: "STEM Lab Adventures", desc: "Exciting science experiments, robotics demonstrations, and coding showcases.", category: "STEM", color: "bg-cyan-100 text-cyan-600" },
    { title: "Student Talent Show", desc: "Singing, dancing, comedy, spoken word, and more from our talented students.", category: "Entertainment", color: "bg-amber-100 text-amber-600" },
    { title: "Teacher Spotlights", desc: "Get to know our amazing teachers - their stories, passions, and teaching philosophies.", category: "Features", color: "bg-teal-100 text-teal-600" },
  ];
  return (
    <PageShell icon={Tv} title="School TV" subtitle="Watch, learn, and relive school moments" color="from-blue-600 to-indigo-600">
      {/* LIVE TV WINDOW */}
      <Card className="mb-8 overflow-hidden shadow-2xl border-0" data-testid="live-tv-window">
        <div className="relative bg-gradient-to-br from-[#0a0a1a] via-[#0d1b3e] to-[#0a0a1a] aspect-video max-h-[520px]">
          {/* TV Frame */}
          <div className="absolute inset-0 border-[6px] border-gray-800 rounded-xl" />
          <div className="absolute inset-[6px] border border-gray-700/50 rounded-lg" />

          {/* Screen Content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {/* Scan lines effect */}
            <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)" }} />

            {/* LIVE Badge */}
            <div className="absolute top-6 left-6 flex items-center gap-2">
              <div className="flex items-center gap-1.5 bg-red-600 text-white px-3 py-1 rounded-md text-xs font-bold animate-pulse">
                <div className="w-2 h-2 bg-white rounded-full" />
                LIVE
              </div>
              <span className="text-white/50 text-xs">YCPS School TV</span>
            </div>

            {/* Channel info */}
            <div className="absolute top-6 right-6 text-right">
              <span className="text-white/40 text-xs">CH 1</span>
            </div>

            {/* Center Play / Logo */}
            {!isPlaying ? (
              <div className="text-center">
                <div className="w-32 h-32 mx-auto mb-4 bg-white/5 rounded-full flex items-center justify-center border-2 border-white/10 backdrop-blur-sm">
                  <Tv size={48} className="text-white/30" />
                </div>
                <h2 className="text-white text-xl font-bold mb-1">YCPS School TV</h2>
                <p className="text-white/50 text-sm mb-6">Young Christian Preparatory School Broadcasting</p>
                <button onClick={() => setIsPlaying(true)}
                  className={`px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-2xl ${btn3DStyle} cursor-pointer select-none flex items-center gap-3 mx-auto`}
                  data-testid="tv-play-btn">
                  <div className="w-0 h-0 border-l-[16px] border-l-white border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent" />
                  Watch Live
                </button>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-24 h-24 mx-auto mb-4 bg-white/5 rounded-full flex items-center justify-center border border-white/10 animate-pulse">
                  <Tv size={36} className="text-blue-400" />
                </div>
                <h2 className="text-white text-lg font-semibold mb-1">No Live Broadcast Right Now</h2>
                <p className="text-white/40 text-sm mb-2">The next live broadcast will be announced soon</p>
                <p className="text-white/30 text-xs">Check the schedule below or browse recorded content</p>
                <button onClick={() => setIsPlaying(false)}
                  className={`mt-4 px-6 py-2 bg-white/10 text-white/70 font-medium rounded-xl ${btn3DStyle} cursor-pointer select-none border border-white/10`}
                  data-testid="tv-back-btn">
                  Back to Home Screen
                </button>
              </div>
            )}

            {/* Bottom info bar */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#FF6B00] rounded-lg flex items-center justify-center">
                  <Tv size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">YCPS TV</p>
                  <p className="text-white/50 text-xs">School Broadcasting Channel</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-white/40 text-xs">
                <Clock size={12} /> Live &middot; Takoradi, Ghana
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Recorded Content */}
      <h2 className="text-xl font-bold text-[#002147] mb-4 flex items-center gap-2"><Video size={22} /> Recorded Content</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {channels.map((ch, i) => (
          <Card key={i} className="overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer group" data-testid={`tv-channel-${i}`}>
            <div className="h-36 bg-gradient-to-br from-[#002147] to-[#003366] flex items-center justify-center relative overflow-hidden">
              <Video size={40} className="text-white/20 absolute" />
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <div className="w-0 h-0 border-l-[14px] border-l-white border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent ml-1" />
              </div>
              <Badge className="absolute top-3 right-3 bg-white/20 text-white">{ch.category}</Badge>
            </div>
            <CardContent className="p-4">
              <h3 className="font-bold text-[#002147] mb-1">{ch.title}</h3>
              <p className="text-sm text-gray-600">{ch.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="mt-6 bg-gradient-to-r from-[#FF6B00] to-orange-500 text-white">
        <CardContent className="p-6 text-center">
          <h3 className="font-bold text-lg mb-2">Submit Your Video</h3>
          <p className="text-white/80 text-sm">Have a school event recording or creative video? Email it to media@ycpschool.com for a chance to be featured!</p>
        </CardContent>
      </Card>
    </PageShell>
  );
};
